import { useCallback, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type FocusEvent, type KeyboardEvent } from 'react'
import { useIntl } from 'react-intl'

import { cn, TextField } from '@/shared/ui'

import type { FilterSuggestions } from './definition'
import { Highlight, type HighlightTerm } from './highlight'
import { useSuggestions } from './use-suggestions'

/**
 * A filter's value with the values it may take suggested as the viewer types (the mockups'
 * `FilterCombobox`): an ARIA combobox whose list sits under the field, in the panel's flow, so the
 * panel's scroll never clips it. Down and Up move through the suggestions, Enter or a click picks
 * one, and Escape closes the list but not the panel. Focus stays in the field, which points at the
 * highlighted suggestion with `aria-activedescendant`, and how many were found is announced.
 */
export function SuggestField(props: {
  /** The field's own name, such as "Organization: value". */
  readonly label: string
  /** The filter's name, for the list's. */
  readonly fieldLabel: string
  readonly placeholder: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly source: FilterSuggestions
  /** The search shows inactive records, so the list suggests them too. */
  readonly includeInactive: boolean
}) {
  const intl = useIntl()
  const { value, onChange } = props
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const [isOpen, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const movedByKey = useRef(false)
  const suggestions = useSuggestions(props.source, isOpen ? value : '', props.includeInactive)
  const values = useMemo(() => (suggestions.status === 'ready' ? suggestions.values : []), [suggestions])
  const isExpanded = isOpen && values.length > 0
  const activeIndex = isExpanded && active < values.length ? active : -1
  const optionId = (index: number) => `${listId}-${String(index)}`
  const typed = useMemo<readonly HighlightTerm[]>(() => [{ text: value, match: 'contains' }], [value])

  // Focus stays in the field, so nothing else brings the highlighted suggestion into view.
  useEffect(() => {
    if (movedByKey.current && activeIndex >= 0) {
      movedByKey.current = false
      document.getElementById(`${listId}-${String(activeIndex)}`)?.scrollIntoView?.({ block: 'nearest' })
    }
  }, [activeIndex, listId])

  const pick = useCallback(
    (picked: string) => {
      onChange(picked)
      setOpen(false)
      setActive(-1)
    },
    [onChange],
  )
  const change = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value)
      setOpen(true)
      setActive(-1)
    },
    [onChange],
  )
  const keyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      // Keys that finish an input method's text, as for Japanese or Chinese, are its own.
      if (event.nativeEvent.isComposing) {
        return
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        if (!isExpanded) {
          setOpen(true)
          return
        }
        movedByKey.current = true
        const last = values.length - 1
        setActive(event.key === 'ArrowDown' ? (activeIndex >= last ? 0 : activeIndex + 1) : activeIndex <= 0 ? last : activeIndex - 1)
      } else if (event.key === 'Enter' && activeIndex >= 0) {
        const picked = values[activeIndex]
        if (picked !== undefined) {
          event.preventDefault()
          pick(picked)
        }
      } else if (event.key === 'Escape' && isOpen) {
        // Closes the suggestions only, cancelling any still asked for; the next Escape closes the panel.
        event.preventDefault()
        event.stopPropagation()
        setOpen(false)
        setActive(-1)
      }
    },
    [isOpen, isExpanded, values, activeIndex, pick],
  )
  const blur = useCallback((event: FocusEvent) => {
    if (!(event.relatedTarget instanceof Node && rootRef.current?.contains(event.relatedTarget) === true)) {
      setOpen(false)
      setActive(-1)
    }
  }, [])

  const announcement =
    isOpen && suggestions.status === 'ready' ? intl.formatMessage({ id: 'search.suggestions.count' }, { count: values.length }) : ''

  return (
    <div ref={rootRef} onBlur={blur} className="flex flex-col gap-1">
      <TextField
        label={props.label}
        hideLabel
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- it is an input, which WAI-ARIA's combobox pattern names a combobox
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isExpanded}
        aria-controls={listId}
        {...(activeIndex >= 0 && { 'aria-activedescendant': optionId(activeIndex) })}
        autoComplete="off"
        value={value}
        onChange={change}
        onKeyDown={keyDown}
        placeholder={props.placeholder}
      />
      {/* Always present, hidden while it is closed, so the field always names what it controls. */}
      <ul
        id={listId}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role, jsx-a11y/no-noninteractive-element-to-interactive-role -- a select or datalist cannot show the marked text the pattern's list does
        role="listbox"
        hidden={!isExpanded}
        aria-label={intl.formatMessage({ id: 'search.suggestions.list' }, { field: props.fieldLabel })}
        className="max-h-48 overflow-y-auto rounded border border-line bg-background py-1"
      >
        {isExpanded &&
          values.map((suggestion, index) => (
            <SuggestionOption
              key={suggestion}
              id={optionId(index)}
              value={suggestion}
              terms={typed}
              isActive={index === activeIndex}
              onPick={pick}
            />
          ))}
      </ul>
      {isOpen && suggestions.status === 'ready' && values.length === 0 && (
        <p className="px-1 text-sm text-text-secondary">{intl.formatMessage({ id: 'search.suggestions.none' })}</p>
      )}
      {/* Always present, so a screen reader hears how many suggestions each text brings. */}
      <output className="sr-only">{announcement}</output>
    </div>
  )
}

function SuggestionOption(props: {
  readonly id: string
  readonly value: string
  readonly terms: readonly HighlightTerm[]
  readonly isActive: boolean
  readonly onPick: (value: string) => void
}) {
  const { value, onPick } = props
  const click = useCallback(() => onPick(value), [onPick, value])
  return (
    // Picked by the field's keys (Enter), or by a click; focus never leaves the field.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- the combobox's field takes the keys
    <li
      id={props.id}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role, jsx-a11y/no-noninteractive-element-to-interactive-role -- an option of the combobox's listbox
      role="option"
      aria-selected={props.isActive}
      // A press would blur the field and close the list before the click lands.
      onMouseDown={preventDefault}
      onClick={click}
      className={cn(
        'flex min-h-9 cursor-pointer items-center px-3 py-1.5 text-sm text-text-primary hover:bg-background-contrast-sm',
        props.isActive && 'bg-background-contrast-sm outline-2 -outline-offset-2 outline-primary-main',
      )}
    >
      <span className="min-w-0 break-words">
        <Highlight text={value} terms={props.terms} />
      </span>
    </li>
  )
}

function preventDefault(event: { preventDefault: () => void }) {
  event.preventDefault()
}
